import { FC, useMemo } from "react";
import { ChatUserInfo } from "@xapp/stentor-chat-widget";
import { ChatMsgDetail, ToolCallInfo } from "../../store/ChatAction";
import { isAgent } from "../../utils";
import { ChatMessageBubble } from "../ChatMessageBubble";
import { ToolCallDisplay } from "../ToolCallDisplay";
import "./ChatTextMessage.scss";

/**
 * Escape &, <, > for safe insertion into HTML text nodes.
 * Not suitable for attribute values.
 */
function escapeHtml(text: string): string {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function parseInlineMarkdown(text: string): string {
    return escapeHtml(text)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/__(.+?)__/g, "<strong>$1</strong>")
        .replace(/\*([^*]+?)\*/g, "<em>$1</em>")
        .replace(/_([^_]+?)_/g, "<em>$1</em>");
}

function tableToHtml(headerLine: string, separatorLine: string, bodyBlock: string): string {
    const parseRow = (row: string) =>
        row.split("|").slice(1, -1).map(cell => parseInlineMarkdown(cell.trim()));

    const separators = separatorLine.split("|").slice(1, -1).map(s => s.trim());
    const alignments = separators.map(sep => {
        if (sep.startsWith(":") && sep.endsWith(":")) return "center";
        if (sep.endsWith(":")) return "right";
        return "left";
    });

    const headers = parseRow(headerLine);
    const headerHtml = headers.map((h, i) =>
        `<th scope="col" style="text-align:${alignments[i] || "left"}">${h}</th>`
    ).join("");

    const bodyRows = bodyBlock.trim().split("\n").filter(row => row.trim() !== "");
    const bodyHtml = bodyRows.map(row => {
        const cells = parseRow(row);
        return "<tr>" + cells.map((c, i) =>
            `<td style="text-align:${alignments[i] || "left"}">${c}</td>`
        ).join("") + "</tr>";
    }).join("");

    return `<div style="overflow-x:auto"><table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`;
}

export function parseMarkdown(text: string): string {
    if (!text) return "";

    // Extract tables first, replace with placeholders, then process the rest
    const tables: string[] = [];
    let processed = text.replace(
        /(?:^|\n)(\|.+\|)\n(\|[\s:|-]+\|)\n((?:\|.+\|\n?)+)/g,
        (_match, headerLine: string, separatorLine: string, bodyBlock: string) => {
            tables.push(tableToHtml(headerLine, separatorLine, bodyBlock));
            return `\n%%TABLE${tables.length - 1}%%\n`;
        }
    );

    // Now safely escape and parse markdown on the non-table text
    processed = processed
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        // Headers (must be at start of line)
        .replace(/^### (.+)$/gm, "<h4>$1</h4>")
        .replace(/^## (.+)$/gm, "<h3>$1</h3>")
        .replace(/^# (.+)$/gm, "<h2>$1</h2>")
        // Bold (** or __)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/__(.+?)__/g, "<strong>$1</strong>")
        // Italic (* or _) - must come after bold
        .replace(/\*([^*]+?)\*/g, "<em>$1</em>")
        .replace(/_([^_]+?)_/g, "<em>$1</em>")
        // Links [text](url)
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        // Unordered lists (- or *)
        .replace(/^[-*] (.+)$/gm, "<li>$1</li>")
        // Line breaks
        .replace(/\n/g, "<br>");

    // Wrap consecutive <li> elements in <ul>
    processed = processed.replace(/(<li>.*?<\/li>)(<br>)?/g, "$1");
    processed = processed.replace(/(<li>.*?<\/li>)+/g, "<ul>$&</ul>");
    // Clean up <br> inside <ul>
    processed = processed.replace(/<ul>(.*?)<\/ul>/g, (match) => {
        return match.replace(/<br>/g, "");
    });

    // Restore table placeholders (strip adjacent <br> tags)
    tables.forEach((tableHtml, i) => {
        processed = processed.replace(new RegExp(`(?:<br>)?%%TABLE${i}%%(?:<br>)?`), tableHtml);
    });

    return processed;
}

export interface ChatTextMessageProps {
    readonly message: ChatMsgDetail;
    readonly sibling: boolean;
    /** Agent/user info for avatar rendering */
    readonly user?: ChatUserInfo;
    /** Tool calls to display above the message bubble */
    readonly toolCalls?: ToolCallInfo[];
    /** When true, shows expanded debug info for tool calls */
    readonly debugMode?: boolean;
}

export const ChatTextMessage: FC<ChatTextMessageProps> = (props) => {
    const message = props.message;
    const date = new Date(message.timestamp);

    const time = date.getHours() + ":" + date.getMinutes();

    const agentMessage = isAgent(props.message.user?.nick);
    const isStreaming = message.isStreaming;

    // Parse markdown for agent messages
    const parsedHtml = useMemo(() => {
        if (agentMessage && message.msg.text) {
            return parseMarkdown(message.msg.text);
        }
        return null;
    }, [agentMessage, message.msg.text]);

    // No tail for streaming messages (or messages that were streamed)
    const wasStreamed = message.wasStreamed;
    const showTail = agentMessage && !props.sibling && !isStreaming && !wasStreamed;

    const hasToolCalls = props.toolCalls && props.toolCalls.length > 0;

    return (
        <div className={`chat-msg${isStreaming ? " chat-msg--streaming" : ""}`}>
            {/* Tool progress above the bubble */}
            {hasToolCalls && (
                <ToolCallDisplay toolCalls={props.toolCalls} debugMode={props.debugMode} />
            )}
            <div className="chat-text-container">
                <ChatMessageBubble
                    owner={agentMessage ? "others" : "mine"}
                    hasTail={showTail}>
                    <span className="message-sr-only">
                        {"at " + time + (agentMessage ? (isStreaming ? " the bot is typing" : " the bot said") : " the user said")}
                    </span>
                    {parsedHtml ? (
                        <span className="chat-msg__content" dangerouslySetInnerHTML={{ __html: parsedHtml }} />
                    ) : (
                        <span className="chat-msg__content">{message.msg.text}</span>
                    )}
                    {isStreaming && <span className="streaming-cursor" aria-hidden="true" />}
                </ChatMessageBubble>
            </div>
        </div>
    );
};
